// https://github.com/rigor789/vue-scrollto/blob/master/src/scrollTo.js

import BezierEasing from '@common/utils/gsap/bezierEasing'

const abortEvents = [
  'mousedown',
  'wheel',
  'DOMMouseScroll',
  'mousewheel',
  'keyup',
  'touchmove'
]

let defaults = {
  container: 'body',
  duration: 500,
  easing: 'ease-in-out',
  offset: 0,
  force: true,
  cancelable: true,
  onStart: false,
  onDone: false,
  onCancel: false,
  x: false,
  y: true
}

export function setDefaults (options) {
  defaults = Object.assign({}, defaults, options)
}

function on (element, events, handler, opts = { passive: false }) {
  if (!(events instanceof Array)) {
    events = [events]
  }

  for (let i = 0; i < events.length; i++) {
    element.addEventListener(
      events[i],
      handler,
      opts
    )
  }
}

function off (element, events, handler) {
  if (!(events instanceof Array)) {
    events = [events]
  }

  for (let i = 0; i < events.length; i++) {
    element.removeEventListener(events[i], handler)
  }
}

function cumulativeOffset (element) {
  let top = 0
  let left = 0

  do {
    top += element.offsetTop || 0
    left += element.offsetLeft || 0
    element = element.offsetParent
  } while (element)

  return {
    top: top,
    left: left
  }
}

export const scroller = () => {
  let element // element to scroll to
  let container // container to scroll
  let duration // duration of the scrolling
  let easing // easing to be used when scrolling
  let offset // offset to be added (subtracted)
  let force // force scroll, even if element is visible
  let cancelable // indicates if user can cancel the scroll or not.
  let onStart // callback when scrolling is started
  let onDone // callback when scrolling is done
  let onCancel // callback when scrolling is canceled / aborted
  let x // scroll on x axis
  let y // scroll on y axis

  let initialX // initial X of container
  let targetX // target X of container
  let initialY // initial Y of container
  let targetY // target Y of container
  let diffX // difference
  let diffY // difference

  let abort // is scrolling aborted

  let abortEv // event that aborted scrolling
  let abortFn = e => {
    if (!cancelable) {
      return
    }

    abortEv = e
    abort = true
  }

  let easingFn

  let timeStart // time when scrolling started
  let timeElapsed // time elapsed since scrolling started

  let progress // progress

  function scrollTop (container) {
    let scrollTop = container.scrollTop

    if (container.tagName.toLowerCase() === 'body') {
      // in firefox body.scrollTop always returns 0
      // thus if we are trying to get scrollTop on a body tag
      // we need to get it from the documentElement
      scrollTop = scrollTop || document.documentElement.scrollTop
    }

    return scrollTop
  }

  function scrollLeft (container) {
    let scrollLeft = container.scrollLeft

    if (container.tagName.toLowerCase() === 'body') {
      // in firefox body.scrollLeft always returns 0
      // thus if we are trying to get scrollLeft on a body tag
      // we need to get it from the documentElement
      scrollLeft = scrollLeft || document.documentElement.scrollLeft
    }

    return scrollLeft
  }

  function step (timestamp) {
    if (abort) {
      return done()
    }

    if (!timeStart) {
      timeStart = timestamp
    }

    timeElapsed = timestamp - timeStart

    progress = Math.min(timeElapsed / duration, 1)
    progress = easingFn(progress)

    topLeft(container, initialY + diffY * progress, initialX + diffX * progress)

    if (timeElapsed < duration) {
      window.requestAnimationFrame(step)
    } else {
      done()
    }
  }

  function done () {
    if (!abort) {
      topLeft(container, targetY, targetX)
    }

    timeStart = false

    off(container, abortEvents, abortFn)

    if (abort && onCancel) {
      onCancel(abortEv, element)
    }

    if (!abort && onDone) {
      onDone(element)
    }
  }

  function topLeft (element, top, left) {
    if (y) {
      element.scrollTop = top
    }
    if (x) {
      element.scrollLeft = left
    }
    if (element.tagName.toLowerCase() === 'body') {
      // in firefox body.scrollTop doesn't scroll the page
      // thus if we are trying to scrollTop on a body tag
      // we need to scroll on the documentElement
      if (y) {
        document.documentElement.scrollTop = top
      }
      if (x) {
        document.documentElement.scrollLeft = left
      }
    }
  }

  function scrollTo (target, options = {}) {
    options = Object.keys(options).length > 0
      ? { ...defaults, ...options }
      : defaults

    // if (!target) {
    //   return console.warn(
    //     'Trying to scroll to an element that is not on the page: ' +
    //     target
    //   )
    // }

    container = document.querySelector(options.container)

    duration = options.duration
    easing = options.easing
    offset = options.offset
    force = options.force !== false
    cancelable = options.cancelable !== false
    onStart = options.onStart
    onDone = options.onDone
    onCancel = options.onCancel
    x = options.x
    y = options.y

    let cumulativeOffsetContainer = cumulativeOffset(container)
    let cumulativeOffsetElement = cumulativeOffset(target)

    if (typeof offset === 'function') {
      offset = offset(element, container)
    }

    initialY = scrollTop(container)
    targetY = cumulativeOffsetElement.top - cumulativeOffsetContainer.top + offset

    initialX = scrollLeft(container)
    targetX = cumulativeOffsetElement.left - cumulativeOffsetContainer.left + offset

    abort = false

    diffY = targetY - initialY
    diffX = targetX - initialX

    if (!force) {
      // When the container is the default (body) we need to use the viewport
      // height, not the entire body height
      const containerHeight = container.tagName.toLowerCase() === 'body'
        ? document.documentElement.clientHeight || window.innerHeight
        : container.offsetHeight
      const containerTop = initialY
      const containerBottom = containerTop + containerHeight
      const elementTop = targetY - offset
      const elementBottom = elementTop + target.offsetHeight

      if (elementTop >= containerTop && elementBottom <= containerBottom) {
        // make sure to call the onDone callback even if there is no need to
        // scroll the container. Fixes #111 (ref #118)
        if (onDone) {
          onDone(element)
        }

        return
      }
    }

    if (onStart) {
      onStart(element)
    }

    if (!diffY && !diffX) {
      if (onDone) {
        onDone(element)
      }

      return
    }

    if (typeof easing === 'string') {
      easingFn = BezierEasing.css[easing]
    } else {
      easingFn = BezierEasing(
        easing.x1,
        easing.y1,
        easing.x2,
        easing.y2
      )
    }

    on(container, abortEvents, abortFn, { passive: true })

    window.requestAnimationFrame(step)

    return () => {
      abortEv = null
      abort = true
    }
  }

  return scrollTo
}

const _scroller = scroller()
export default _scroller
